import { FC, useState } from "react";
import { Pressable } from "react-native";
import { CheckIcon, ChevronRightIcon } from "lucide-react-native";

import { DocumentGroupEnumType } from "@libs/api/patients";
import { VnBadge } from "@/components/VnBadge/VnBadge";
import { VnBox } from "@/components/VnBox";
import { VnIcon } from "@/components/VnIcon";
import { VnProgress } from "@/components/VnProgress";
import { VnSeparator } from "@/components/VnSeparator";
import { VnSheet, VnSheetContent } from "@/components/VnSheet";
import { VnText } from "@/components/VnText/VnText";
import { useListDocumentRequirementsQuery } from "@/data/queries/uploadedDocumentsQueries";
import { labelForDocumentGroup } from "@/domain/uploadedDocuments";
import { useTranslator } from "@/hooks/useTranslate";
import { DocumentsSheet } from "@/interactors/DocumentsSheet";

import { SideSheetHeader } from "./SideSheetHeader";

type DocumentsSectionPropsType = {
  householdMemberFullName: string;
  householdMemberId: string;
  context?: "application" | "profile";
};

export const DocumentsSection: FC<DocumentsSectionPropsType> = ({
  householdMemberId,
  householdMemberFullName,
  context = "application",
}) => {
  const { t } = useTranslator();
  const [documentGroup, setDocumentGroup] =
    useState<DocumentGroupEnumType | null>(null);

  const { data: requirements } = useListDocumentRequirementsQuery();

  const memberRequirements = requirements?.find(
    member => member.householdMemberId === householdMemberId,
  );

  const sections = memberRequirements?.sections ?? [];

  const visibleSections =
    context === "profile"
      ? sections
      : sections.filter(section => section.isApplicable);

  return (
    <>
      {memberRequirements && memberRequirements.applicableCount > 0 && (
        <VnBox className="gap-2">
          <VnText
            variant="sm"
            className="text-muted-foreground"
            i18nKey="native:applicantDocuments.progress"
            i18nOptions={{
              completeCount: memberRequirements.completeCount,
              applicableCount: memberRequirements.applicableCount,
            }}
          />
          <VnProgress
            value={
              (memberRequirements.completeCount /
                memberRequirements.applicableCount) *
              100
            }
          />
        </VnBox>
      )}

      {visibleSections.map((section, index) => (
        <Pressable
          key={section.group}
          onPress={() => setDocumentGroup(section.group)}
          className="gap-4">
          <VnBox className="flex-row justify-between gap-2">
            <VnBox className="flex-1 flex-row items-center gap-2">
              {section.isComplete && (
                <VnBox className="bg-border h-6 w-6 flex-shrink-0 items-center justify-center rounded-full">
                  <VnIcon as={CheckIcon} />
                </VnBox>
              )}
              <VnText
                variant="lg"
                className="flex-1"
                i18nKey={labelForDocumentGroup(section.group)}
              />
              {!section.isApplicable && (
                <VnBadge
                  variant="secondary"
                  i18nKey="native:applicantDocuments.optional"
                />
              )}
            </VnBox>
            <VnBox className="flex-shrink-0">
              <VnIcon
                as={ChevronRightIcon}
                size={20}
                className="text-foreground"
              />
            </VnBox>
          </VnBox>
          {index < visibleSections.length - 1 && <VnSeparator />}
        </Pressable>
      ))}

      <VnSheet
        open={Boolean(documentGroup)}
        shouldShowCloseButton={false}
        overlayClassName="z-0"
        onOpenChange={isOpen => {
          if (!isOpen) {
            setDocumentGroup(null);
          }
        }}>
        <VnSheetContent className="md:max-w-1/3">
          <SideSheetHeader
            title={householdMemberFullName}
            subtitle={t(
              documentGroup ? labelForDocumentGroup(documentGroup) : "",
            )}
          />
          {documentGroup && (
            <DocumentsSheet
              householdMemberId={householdMemberId}
              documentGroup={documentGroup}
              context={context}
            />
          )}
        </VnSheetContent>
      </VnSheet>
    </>
  );
};
